import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { listTasks, createTask, updateTask, deleteTask } from "@/lib/api";
import { Plus, Trash2, Check, Clock, AlertCircle, X, Calendar as CalIcon, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { MentionInput } from "@/components/admin/MentionInput";
import { MentionText } from "@/components/admin/MentionText";

const PRIORITY_STYLES = {
  low: "bg-stone-100 text-stone-700 border-stone-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  high: "bg-red-50 text-red-800 border-red-200",
};

const STATUS_STYLES = {
  open: "bg-white border-stone-300 text-stone-700",
  in_progress: "bg-ccc-purple/10 border-ccc-purple/30 text-ccc-plum",
  done: "bg-emerald-50 border-emerald-200 text-emerald-800 line-through opacity-60",
  cancelled: "bg-stone-50 border-stone-200 text-stone-400 line-through",
};

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState({ assignee: "me", status: "" });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => listTasks(filter).then(setTasks);
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line

  const overdueDate = new Date().toISOString().slice(0, 10);
  const grouped = useMemo(() => {
    const g = { overdue: [], today: [], upcoming: [], no_due: [], done: [] };
    tasks.forEach(t => {
      if (t.status === "done" || t.status === "cancelled") g.done.push(t);
      else if (!t.due_date) g.no_due.push(t);
      else if (t.due_date < overdueDate) g.overdue.push(t);
      else if (t.due_date === overdueDate) g.today.push(t);
      else g.upcoming.push(t);
    });
    return g;
  }, [tasks, overdueDate]);

  const toggleStatus = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    await updateTask(t.id, { status: next });
    toast.success(next === "done" ? "Task completed" : "Task reopened");
    load();
  };

  const remove = async (t) => {
    if (!confirm(`Delete "${t.title}"?`)) return;
    await deleteTask(t.id);
    toast.success("Task deleted");
    load();
  };

  return (
    <div data-testid="admin-tasks-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-ccc-gold">Getting Things Done</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Tasks</h1>
          <div className="text-sm text-stone-600 mt-1">
            Assign, track, and finish. Due-dated tasks auto-sync to the team Google Calendar.
          </div>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="tasks-new-button"
          className="inline-flex items-center gap-2 bg-ccc-plum hover:bg-ccc-purple text-ccc-ivory px-4 py-2 text-sm uppercase tracking-wider font-semibold"
        >
          <Plus size={14}/> New Task
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-5">
        <select
          value={filter.assignee}
          onChange={e => setFilter({ ...filter, assignee: e.target.value })}
          data-testid="tasks-assignee-filter"
          className="px-3 py-1.5 border border-stone-300 bg-white text-sm focus:outline-none focus:border-ccc-purple"
        >
          <option value="me">Assigned to me</option>
          <option value="">Assigned to anyone</option>
        </select>
        <select
          value={filter.status}
          onChange={e => setFilter({ ...filter, status: e.target.value })}
          data-testid="tasks-status-filter"
          className="px-3 py-1.5 border border-stone-300 bg-white text-sm focus:outline-none focus:border-ccc-purple"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="done">Done</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="text-xs text-stone-500 ml-auto">
          {tasks.length} task{tasks.length === 1 ? "" : "s"}
        </div>
      </div>

      {[
        { key: "overdue", label: "Overdue", tint: "text-red-700" },
        { key: "today", label: "Due today", tint: "text-amber-700" },
        { key: "upcoming", label: "Upcoming", tint: "text-ccc-plum" },
        { key: "no_due", label: "No due date", tint: "text-stone-600" },
        { key: "done", label: "Completed", tint: "text-emerald-700" },
      ].map(section => grouped[section.key].length > 0 && (
        <section key={section.key} className="mb-6" data-testid={`tasks-section-${section.key}`}>
          <h3 className={`font-serif text-lg mb-2 ${section.tint}`}>
            {section.label} <span className="text-xs text-stone-500 font-sans">({grouped[section.key].length})</span>
          </h3>
          <ul className="space-y-2">
            {grouped[section.key].map(t => (
              <TaskRow key={t.id} task={t}
                onToggle={() => toggleStatus(t)}
                onEdit={() => setEditing(t)}
                onDelete={() => remove(t)}
              />
            ))}
          </ul>
        </section>
      ))}

      {tasks.length === 0 && (
        <div className="text-center py-16 text-stone-500" data-testid="tasks-empty">
          <div className="text-4xl mb-2">✓</div>
          <div className="font-serif text-2xl text-ccc-plum mb-1">Inbox zero.</div>
          <div className="text-sm">No tasks match this filter. Create one to get started.</div>
        </div>
      )}

      {(creating || editing) && (
        <TaskModal
          task={editing}
          defaultAssignee={user?.email}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onEdit, onDelete }) {
  const isDone = task.status === "done";
  return (
    <li
      className={`border p-3 flex items-start gap-3 ${STATUS_STYLES[task.status] || "bg-white border-stone-200"}`}
      data-testid={`task-row-${task.id}`}
    >
      <button
        onClick={onToggle}
        aria-label={isDone ? "Mark as open" : "Mark as done"}
        data-testid={`task-toggle-${task.id}`}
        className={`mt-0.5 w-5 h-5 border ${isDone ? "bg-emerald-500 border-emerald-500" : "border-stone-400 hover:border-ccc-purple"} flex items-center justify-center`}
      >
        {isDone && <Check size={12} className="text-white"/>}
      </button>
      <div className="flex-1 cursor-pointer" onClick={onEdit}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{task.title}</span>
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${PRIORITY_STYLES[task.priority] || ""}`}>
            {task.priority}
          </span>
          {task.google_event_id && <CalIcon size={11} className="text-ccc-purple" aria-label="Synced to Google Calendar"/>}
        </div>
        <div className="text-xs text-stone-600 mt-1 flex flex-wrap items-center gap-3">
          {task.due_date && (
            <span className="inline-flex items-center gap-1"><Clock size={11}/>{task.due_date}</span>
          )}
          {task.assignee_email && (
            <span className="inline-flex items-center gap-1"><UserIcon size={11}/>{task.assignee_email}</span>
          )}
          {task.related_type && (
            <span className="text-ccc-purple">→ {task.related_type}</span>
          )}
        </div>
        {task.description && <div className="text-xs text-stone-500 mt-1"><MentionText text={task.description}/></div>}
      </div>
      <button
        onClick={onDelete}
        aria-label="Delete task"
        data-testid={`task-delete-${task.id}`}
        className="text-stone-400 hover:text-red-600 p-1"
      >
        <Trash2 size={14}/>
      </button>
    </li>
  );
}

function TaskModal({ task, defaultAssignee, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task?.title || "",
    description: task?.description || "",
    due_date: task?.due_date || "",
    priority: task?.priority || "medium",
    status: task?.status || "open",
    assignee_email: task?.assignee_email || defaultAssignee || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      const payload = { ...form, due_date: form.due_date || null, assignee_email: form.assignee_email || null };
      if (task) await updateTask(task.id, payload);
      else await createTask(payload);
      toast.success(task ? "Task updated" : "Task created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="task-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="bg-white w-full max-w-lg shadow-2xl"
      >
        <div className="p-5 bg-ccc-plum text-ccc-ivory flex justify-between items-center">
          <h3 className="font-serif text-2xl">{task ? "Edit task" : "New task"}</h3>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="task-modal-close"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-3">
          <input
            required
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="Task title"
            data-testid="task-title"
            className="w-full px-3 py-2 border border-stone-300 text-sm focus:outline-none focus:border-ccc-purple"
          />
          <MentionInput
            rows={3}
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            placeholder="Description — type @ to mention a teammate"
            data-testid="task-description"
            className="w-full px-3 py-2 border border-stone-300 text-sm focus:outline-none focus:border-ccc-purple font-serif resize-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-stone-600 mb-1 block">Due date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={e => setForm({ ...form, due_date: e.target.value })}
                data-testid="task-due-date"
                className="w-full px-3 py-2 border border-stone-300 text-sm focus:outline-none focus:border-ccc-purple"
              />
            </div>
            <div>
              <label className="text-xs text-stone-600 mb-1 block">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                data-testid="task-priority"
                className="w-full px-3 py-2 border border-stone-300 bg-white text-sm focus:outline-none focus:border-ccc-purple"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-stone-600 mb-1 block">Assignee email</label>
            <input
              type="email"
              value={form.assignee_email}
              onChange={e => setForm({ ...form, assignee_email: e.target.value })}
              placeholder="staffer@cocatholic.org"
              data-testid="task-assignee"
              className="w-full px-3 py-2 border border-stone-300 text-sm focus:outline-none focus:border-ccc-purple"
            />
          </div>
          {task && (
            <div>
              <label className="text-xs text-stone-600 mb-1 block">Status</label>
              <select
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}
                data-testid="task-status"
                className="w-full px-3 py-2 border border-stone-300 bg-white text-sm focus:outline-none focus:border-ccc-purple"
              >
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
        </div>
        <div className="p-5 bg-stone-50 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-stone-600 hover:text-stone-800 px-3 py-2 text-sm uppercase tracking-wider">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="task-save"
            className="bg-ccc-gold hover:bg-ccc-gold-dark text-ccc-plum px-4 py-2 text-sm uppercase tracking-wider font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
